import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  getUploads,
  mapUploadItems,
  getCreations,
  mergeDesignItems,
  MY_DESIGNS_FILE_TYPES,
  MY_DESIGNS_PAGE_SIZE,
  fetchUploadImageBlob,
  fetchCreationImageBlob,
  fetchExportImageBlob,
} from "../../../services/api/editor/background_removal";
import FadeInMedia from "../../ui/FadeInMedia";
import RoundLoader from "../../ui/RoundLoader";
import {
  libraryItemDisplayName,
  libraryItemFormat,
  libraryItemMetaLine,
} from "../../../utils/openLibraryInEditor";
import {
  MAX_UPLOAD_FILE_MB,
  getUploadSizeError,
} from "../../../utils/uploadLimits";
import "./ImagePickerModal.css";

const SOURCE_ALL = "all";
const SOURCE_UPLOADS = "uploads";
const SOURCE_DESIGNS = "designs";
const SOURCE_AI = "ai";

/** Standard BG-removal / editor designs (My Designs “image” type). */
const DESIGNS_FILE_TYPES = "image";
/** AI-generated / AI-tool outputs. */
const AI_IMAGES_FILE_TYPES = "ai_background,upscale,outpaint,passport_photo";

const TABS = [
  { id: SOURCE_ALL, label: "All" },
  { id: SOURCE_UPLOADS, label: "Uploads" },
  { id: SOURCE_DESIGNS, label: "My Designs" },
  { id: SOURCE_AI, label: "AI Images" },
];

const PAGE_SIZE = MY_DESIGNS_PAGE_SIZE;

function sortByCreatedAtDesc(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
}

function emptyMessage(source, hasSearch) {
  if (hasSearch) return "No matches";
  if (source === SOURCE_UPLOADS) return "No uploads yet — add a file above";
  if (source === SOURCE_DESIGNS) return "No designs yet — add a file above";
  if (source === SOURCE_AI) return "No AI images yet";
  return "No images yet — add a file above";
}

function uploadRootId(upload) {
  return (
    upload.root_creation_id ??
    upload.creation_id ??
    Number(String(upload.id).replace(/^upload:/, ""))
  );
}

function designNumericId(design) {
  if (design.kind === "export") {
    return design.export_id ?? Number(String(design.id).replace(/^export:/, ""));
  }
  return design.creation_id ?? Number(String(design.id).replace(/^creation:/, ""));
}

function PickerThumb({ item, source, getToken, onNaturalSize, onFileBytes }) {
  const cardRef = useRef(null);
  const [src, setSrc] = useState(item.output_url || null);
  const [exhausted, setExhausted] = useState(false);
  const onFileBytesRef = useRef(onFileBytes);
  onFileBytesRef.current = onFileBytes;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let observer = null;

    if (item.output_url) {
      setSrc(item.output_url);
      return undefined;
    }

    const load = () => {
      (async () => {
        try {
          const token = await getToken();
          if (!token || cancelled) return;
          let blob;
          if (source === SOURCE_UPLOADS || item.kind === "upload") {
            blob = await fetchUploadImageBlob(token, uploadRootId(item));
          } else if (item.kind === "export") {
            blob = await fetchExportImageBlob(token, designNumericId(item));
          } else {
            blob = await fetchCreationImageBlob(token, designNumericId(item));
          }
          if (cancelled) return;
          if (blob?.type?.includes("json")) throw new Error("Image API JSON error");
          if (typeof blob?.size === "number" && blob.size > 0) {
            onFileBytesRef.current?.(blob.size);
          }
          objectUrl = URL.createObjectURL(blob);
          setSrc(objectUrl);
        } catch {
          if (!cancelled) setExhausted(true);
        }
      })();
    };

    const node = cardRef.current;
    if (!node) {
      load();
      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer?.disconnect();
          load();
        }
      },
      { rootMargin: "160px" }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item, source, getToken]);

  return (
    <div ref={cardRef} className="image-picker__thumb">
      <FadeInMedia
        src={src}
        alt=""
        objectFit="contain"
        className="image-picker__thumb-media"
        loaderSize="sm"
        waiting={!exhausted}
        onLoad={(e) => {
          const img = e?.currentTarget;
          const w = img?.naturalWidth;
          const h = img?.naturalHeight;
          if (w > 0 && h > 0) onNaturalSize?.({ width: w, height: h });
        }}
      />
    </div>
  );
}

/**
 * Dark-theme image library picker.
 * Tabs: All | Uploads | Designs | AI Images — single select, or add a new file.
 */
const ImagePickerModal = ({
  isOpen,
  onClose,
  onPickFile,
  onPickLibrary,
}) => {
  const { getToken } = useAuth();
  const fileInputRef = useRef(null);
  const [source, setSource] = useState(SOURCE_ALL);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [allSkip, setAllSkip] = useState({ uploads: 0, designs: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [measuredSizes, setMeasuredSizes] = useState({});
  const [measuredBytes, setMeasuredBytes] = useState({});
  const [bodyScrolled, setBodyScrolled] = useState(false);

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedId) || null,
    [items, selectedId]
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const name = libraryItemDisplayName(item).toLowerCase();
      const fmt = libraryItemFormat(item).toLowerCase();
      return name.includes(q) || fmt.includes(q) || String(item.id).includes(q);
    });
  }, [items, search]);

  const resetState = useCallback(() => {
    setSource(SOURCE_ALL);
    setSearch("");
    setItems([]);
    setSelectedId(null);
    setError(null);
    setSkip(0);
    setAllSkip({ uploads: 0, designs: 0 });
    setHasMore(false);
    setDragOver(false);
    setMeasuredSizes({});
    setMeasuredBytes({});
    setBodyScrolled(false);
  }, []);

  const loadPage = useCallback(
    async (sourceKey, nextSkip, allCursors = { uploads: 0, designs: 0 }) => {
      const token = await getToken();
      if (!token) throw new Error("Sign in required");

      if (sourceKey === SOURCE_UPLOADS) {
        const data = await getUploads(token, { skip: nextSkip, limit: PAGE_SIZE });
        const mapped = mapUploadItems(data);
        const total =
          data?.total_count ?? data?.total ?? data?.count ?? nextSkip + mapped.length;
        return {
          items: mapped,
          hasMore: nextSkip + mapped.length < total && mapped.length > 0,
          nextSkip: nextSkip + mapped.length,
        };
      }

      if (sourceKey === SOURCE_DESIGNS || sourceKey === SOURCE_AI) {
        const fileTypes =
          sourceKey === SOURCE_AI ? AI_IMAGES_FILE_TYPES : DESIGNS_FILE_TYPES;
        const data = await getCreations(token, {
          skip: nextSkip,
          limit: PAGE_SIZE,
          fileTypes,
          includeExports: sourceKey === SOURCE_DESIGNS,
        });
        const mapped = mergeDesignItems(data).filter(
          (item) => item?.file_type !== "library_upload"
        );
        const total = data?.total ?? nextSkip + mapped.length;
        return {
          items: mapped,
          hasMore: nextSkip + mapped.length < total && mapped.length > 0,
          nextSkip: nextSkip + mapped.length,
        };
      }

      // All — merge uploads + full designs catalog
      const [upData, designData] = await Promise.all([
        getUploads(token, { skip: allCursors.uploads, limit: PAGE_SIZE }),
        getCreations(token, {
          skip: allCursors.designs,
          limit: PAGE_SIZE,
          fileTypes: MY_DESIGNS_FILE_TYPES,
          includeExports: true,
        }),
      ]);
      const uploads = mapUploadItems(upData);
      const designs = mergeDesignItems(designData).filter(
        (item) => item?.file_type !== "library_upload"
      );
      const upTotal =
        upData?.total_count ?? upData?.total ?? upData?.count ?? allCursors.uploads + uploads.length;
      const designTotal = designData?.total ?? allCursors.designs + designs.length;
      const nextUploadSkip = allCursors.uploads + uploads.length;
      const nextDesignSkip = allCursors.designs + designs.length;
      return {
        items: sortByCreatedAtDesc([...uploads, ...designs]),
        hasMore:
          (nextUploadSkip < upTotal && uploads.length > 0) ||
          (nextDesignSkip < designTotal && designs.length > 0),
        nextSkip: 0,
        nextAllSkip: { uploads: nextUploadSkip, designs: nextDesignSkip },
      };
    },
    [getToken]
  );

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    setSelectedId(null);
    setSkip(0);
    setAllSkip({ uploads: 0, designs: 0 });
    setMeasuredSizes({});
    setMeasuredBytes({});
    setBodyScrolled(false);

    loadPage(source, 0, { uploads: 0, designs: 0 })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setHasMore(page.hasMore);
        setSkip(page.nextSkip);
        if (page.nextAllSkip) setAllSkip(page.nextAllSkip);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load images");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, source, loadPage, resetState]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await loadPage(source, skip, allSkip);
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const next =
          source === SOURCE_ALL
            ? sortByCreatedAtDesc([
                ...prev,
                ...page.items.filter((i) => !seen.has(i.id)),
              ])
            : [...prev, ...page.items.filter((i) => !seen.has(i.id))];
        return next;
      });
      setHasMore(page.hasMore);
      setSkip(page.nextSkip);
      if (page.nextAllSkip) setAllSkip(page.nextAllSkip);
    } catch (err) {
      setError(err?.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const confirmLibrary = (item) => {
    if (!item) return;
    onPickLibrary?.(item);
  };

  const confirmSelection = () => {
    if (selectedItem) confirmLibrary(selectedItem);
  };

  const acceptLocalFile = (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    const sizeError = getUploadSizeError(file);
    if (sizeError) {
      setError(sizeError);
      return;
    }
    setError(null);
    onPickFile?.(file);
  };

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    acceptLocalFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    acceptLocalFile(file);
  };

  if (!isOpen) return null;

  return (
    <div className="image-picker-overlay" onClick={onClose} role="presentation">
      <div
        className="image-picker-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add Image"
      >
        <header className="image-picker__header">
          <h2>Add Image</h2>
          <button type="button" className="image-picker__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div
          className={`image-picker__dropzone${dragOver ? " is-dragover" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <button
            type="button"
            className="image-picker__add-btn"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Upload Images
          </button>
          <p>Drag and drop images (max {MAX_UPLOAD_FILE_MB} MB)</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFileInputChange}
          />
        </div>

        <div className="image-picker__filters">
          <div className="image-picker__tabs" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={source === tab.id}
                className={source === tab.id ? "is-active" : ""}
                onClick={() => setSource(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="image-picker__search">
            <span className="image-picker__search-icon" aria-hidden="true">
              ⌕
            </span>
            <input
              type="search"
              placeholder="Search files"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div
          className={`image-picker__body${bodyScrolled ? " is-scrolled" : ""}`}
          onScroll={(e) => {
            const next = e.currentTarget.scrollTop > 2;
            setBodyScrolled((prev) => (prev === next ? prev : next));
          }}
        >
          {loading ? (
            <div className="image-picker__loading">
              <RoundLoader size="md" label="Loading…" />
            </div>
          ) : error ? (
            <p className="image-picker__empty">{error}</p>
          ) : filteredItems.length === 0 ? (
            <p className="image-picker__empty">
              {emptyMessage(source, Boolean(search.trim()))}
            </p>
          ) : (
            <div className="image-picker__grid">
              {filteredItems.map((item) => {
                const selected = item.id === selectedId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`image-picker__card${selected ? " is-selected" : ""}`}
                    onClick={() => setSelectedId(item.id)}
                    onDoubleClick={() => confirmLibrary(item)}
                  >
                    <PickerThumb
                      item={item}
                      source={source}
                      getToken={getToken}
                      onNaturalSize={(size) => {
                        setMeasuredSizes((prev) =>
                          prev[item.id]?.width === size.width &&
                          prev[item.id]?.height === size.height
                            ? prev
                            : { ...prev, [item.id]: size }
                        );
                      }}
                      onFileBytes={(bytes) => {
                        setMeasuredBytes((prev) =>
                          prev[item.id] === bytes
                            ? prev
                            : { ...prev, [item.id]: bytes }
                        );
                      }}
                    />
                    <span className="image-picker__meta">
                      <span className="image-picker__name">{libraryItemDisplayName(item)}</span>
                      <span className="image-picker__fmt">
                        {libraryItemMetaLine(
                          item,
                          measuredSizes[item.id],
                          measuredBytes[item.id]
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && hasMore && !search.trim() && (
            <div className="image-picker__more">
              <button type="button" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>

        <footer className="image-picker__footer">
          <div className="image-picker__storage" aria-label="Storage usage">
            <div className="image-picker__storage-track">
              <div
                className="image-picker__storage-bar"
                style={{ width: "12%" }}
              />
            </div>
            <div className="image-picker__storage-header">
              Storage Used (12GB/100 GB)
            </div>
          </div>
          <div className="image-picker__footer-actions">
            <button type="button" className="image-picker__btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="image-picker__btn-primary"
              disabled={!selectedItem}
              onClick={confirmSelection}
            >
              Done
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ImagePickerModal;
